import { useRef } from 'react'

/**
 * This hook create a proxy to intercept `onClose` and `closePortal`
 * and avoid unnecesary re-renders when user wants to close the portal
 * This is necesary when `onClose` action trigger a router navigation
 * we want to destroy Portal/Modal as soon as possible
 * For that we hold an instance variable `modalClosed` (AKA useRef in hooks)
 */
interface ModalClosefRef {
  current: boolean | null | undefined
}

const buildOnCloseProxy = (onClose: () => void, modalClosed: ModalClosefRef) => () => {
  if (modalClosed.current) return

  modalClosed.current = true
  onClose()
}

interface ClosePortalProxyProps {
  preventOnCloseCallbackAfterClose?: boolean
}

export type ClosePortalFn = (proxyOptions?: ClosePortalProxyProps) => void

interface Props {
  onClose: (() => void) | null | undefined
}

interface ReturnType {
  onClose: (() => void) | null
  closePortalProxy: (closePortal: () => void) => ClosePortalFn
  modalClosed: boolean
}

const useCloseProxy = ({ onClose }: Props): ReturnType => {
  const modalClosed = useRef(false)

  const closePortalProxy =
    (closePortal: () => void) =>
    ({ preventOnCloseCallbackAfterClose = false }: ClosePortalProxyProps = {}) => {
      modalClosed.current = preventOnCloseCallbackAfterClose
      closePortal()
    }

  const onCloseProxy = onClose ? buildOnCloseProxy(onClose, modalClosed) : null

  return {
    onClose: onCloseProxy,
    closePortalProxy,
    modalClosed: !!modalClosed.current,
  }
}

export default useCloseProxy
